/**
 * Property Detail Page
 *
 * Drilldown view for a single property (deduplicated by address_key).
 * Shows property attributes, a price-history chart over snapshot time,
 * and the full snapshot table. Clicking a snapshot row opens a Sheet
 * showing the raw scraped JSON for that snapshot — the deepest entity.
 */

import { Badge } from "@repo/ui/badge";
import { Button } from "@repo/ui/button";
import { Card, CardContent } from "@repo/ui/card";
import {
  CartesianGrid,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  Line,
  LineChart,
  XAxis,
  YAxis,
} from "@repo/ui/chart";
import { cn } from "@repo/ui/lib/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Bath,
  Bed,
  Building2,
  Car,
  DollarSign,
  Download,
  ExternalLink,
  History,
  Loader2,
  MapPin,
  RefreshCw,
  Ruler,
  Sparkles,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { formatDate } from "@/lib/formatDate";
import { getCompleteness, getSaleHistory } from "@/lib/propertyDetail";
import { PropertyEvidence } from "@/components/properties/PropertyEvidence";
import { PropertyAssets } from "@/components/properties/PropertyAssets";
import { PropertyPvReport } from "@/components/properties/PropertyPvReport";
import { fetchPropertyById, fetchPropertySnapshots, normalizePropertyType } from "@/lib/api";

function formatPrice(price: number | null | undefined): string {
  if (!price) return "-";
  if (price >= 1_000_000) return `$${(price / 1_000_000).toFixed(1)}M`;
  if (price >= 1_000) return `$${(price / 1_000).toFixed(0)}K`;
  return `$${price.toLocaleString()}`;
}

const listingTypeConfig: Record<string, { label: string; className: string }> = {
  buy: {
    label: "For Sale",
    className: "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400",
  },
  sold: {
    label: "Sold",
    className: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  },
  rent: {
    label: "For Rent",
    className: "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400",
  },
  auction: {
    label: "Auction",
    className: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400",
  },
};

function ListingTypeBadge({ type }: { type: string }) {
  const config = listingTypeConfig[type.toLowerCase()] ?? {
    label: type,
    className: "bg-gray-100 text-gray-800",
  };
  return <Badge className={cn("text-xs", config.className)}>{config.label}</Badge>;
}

interface RescrapeAttempt {
  url: string;
  adapterSlug: string | null;
  status: "success" | "failed" | "no-adapter";
  provider?: string;
  extracted?: Record<string, unknown>;
  error?: string;
}

const PropertyDetail = () => {
  const { propertyId = "" } = useParams<{ propertyId: string }>();
  const { toast } = useToast();
  const [fetchInfoResult, setFetchInfoResult] = useState<RescrapeAttempt[] | null>(null);

  const propertyQuery = useQuery({
    queryKey: ["property-detail", propertyId],
    queryFn: () => fetchPropertyById(propertyId),
    enabled: !!propertyId,
  });

  const snapshotsQuery = useQuery({
    queryKey: ["property-snapshots", propertyId],
    queryFn: () => fetchPropertySnapshots(propertyId),
    enabled: !!propertyId,
  });

  const property = propertyQuery.data;
  const snapshots = snapshotsQuery.data ?? [];
  const sales = useMemo(() => getSaleHistory(snapshots), [snapshots]);
  const isLoading = propertyQuery.isLoading || snapshotsQuery.isLoading;

  // "Fetch info" — invoke the rescrape-property edge function for a fresh
  // read of every source URL on this property. Preview only; canonical
  // ingestion still goes through process-queue.
  const fetchInfoMutation = useMutation({
    mutationFn: async () => {
      if (!propertyId) throw new Error("No property selected.");
      const { data, error } = await supabase.functions.invoke("rescrape-property", {
        body: { propertyId },
      });
      if (error) throw error;
      return data as { propertyId: string; attempts: RescrapeAttempt[]; note?: string };
    },
    onSuccess: (result) => {
      setFetchInfoResult(result.attempts);
      const wins = result.attempts.filter((a) => a.status === "success").length;
      const total = result.attempts.length;
      toast({
        title: total === 0 ? "Nothing to fetch" : `Fetched ${wins}/${total} sources`,
        description: result.note ?? "Latest extracted fields shown below the snapshot table.",
      });
      // Bump last_scraped_at + scrape_count from the server.
      propertyQuery.refetch();
    },
    onError: (err) => {
      toast({
        title: "Fetch failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    },
  });

  // Build price-history data: prefer sale_price, then asking_price, ordered ascending.
  const chartData = useMemo(() => {
    const points = snapshots
      .filter((s) => s.sale_price ?? s.asking_price)
      .map((s) => ({
        date: s.sale_date ?? s.snapshot_date,
        price: (s.sale_price ?? s.asking_price) as number,
        adapter: s.adapter_slug ?? "unknown",
        snapshotId: s.id,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    return points;
  }, [snapshots]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (propertyQuery.error || snapshotsQuery.error) {
    return (
      <Card>
        <CardContent className="p-8 text-destructive">
          Could not load property evidence: {String(propertyQuery.error ?? snapshotsQuery.error)}
        </CardContent>
      </Card>
    );
  }

  if (!property) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/dashboard/properties">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to properties
          </Link>
        </Button>
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            Property not found.
          </CardContent>
        </Card>
      </div>
    );
  }

  const suburbLink = property.suburb
    ? `/dashboard/suburbs/${encodeURIComponent(property.suburb)}`
    : null;
  const completeness = getCompleteness(property, sales);
  const latestLand = snapshots.find((snapshot) => snapshot.land_size)?.land_size;
  const latestLandUnit = snapshots.find((snapshot) => snapshot.land_size)?.land_size_unit;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <Button variant="ghost" size="sm" asChild className="mb-2 -ml-2">
            <Link to="/dashboard/properties">
              <ArrowLeft className="w-4 h-4 mr-1" />
              Back to properties
            </Link>
          </Button>
          <h1 className="text-2xl font-bold flex items-start gap-2">
            <MapPin className="w-6 h-6 text-muted-foreground flex-shrink-0 mt-1" />
            <span>{property.full_address}</span>
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {suburbLink ? (
              <Link to={suburbLink} className="text-sm font-medium text-primary hover:underline">
                {property.suburb}
              </Link>
            ) : (
              <span className="text-sm font-medium">{property.suburb}</span>
            )}
            {property.state && (
              <Badge variant="outline" className="text-xs">
                {property.state}
              </Badge>
            )}
            {property.postcode && (
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">{property.postcode}</code>
            )}
            {property.listing_type && <ListingTypeBadge type={property.listing_type} />}
            {property.source_adapters.length > 0 && (
              <div className="flex flex-wrap gap-1 ml-2">
                <span className="text-xs text-muted-foreground self-center">reporters:</span>
                {property.source_adapters.map((a) => (
                  <Badge key={a} variant="outline" className="text-xs">
                    {a}
                  </Badge>
                ))}
              </div>
            )}
          </div>
          {property.latitude != null && property.longitude != null && (
            <a
              className="mt-2 inline-block text-xs text-primary hover:underline"
              href={`https://www.openstreetmap.org/?mlat=${property.latitude}&mlon=${property.longitude}#map=17/${property.latitude}/${property.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Coordinates {property.latitude.toFixed(5)}, {property.longitude.toFixed(5)} ·
              OpenStreetMap
            </a>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="default"
            size="sm"
            onClick={() => fetchInfoMutation.mutate()}
            disabled={fetchInfoMutation.isPending}
            title="Run an immediate scrape across every source URL"
          >
            {fetchInfoMutation.isPending ? (
              <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-1.5" />
            )}
            Fetch info
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled
            title="Future value report is not available yet"
          >
            Generate FV report (coming soon)
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => {
              propertyQuery.refetch();
              snapshotsQuery.refetch();
            }}
            disabled={propertyQuery.isFetching || snapshotsQuery.isFetching}
            title="Refresh the local view (does not re-scrape)"
          >
            <RefreshCw
              className={`w-4 h-4 ${propertyQuery.isFetching || snapshotsQuery.isFetching ? "animate-spin" : ""}`}
            />
          </Button>
        </div>
      </div>

      {/* Attributes */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <AttributeTile
          icon={<Building2 className="w-4 h-4 text-muted-foreground" />}
          label="Type"
          value={property.property_type ? normalizePropertyType(property.property_type) : "-"}
        />
        <AttributeTile
          icon={<Bed className="w-4 h-4 text-muted-foreground" />}
          label="Beds"
          value={property.bedrooms?.toString() ?? "-"}
        />
        <AttributeTile
          icon={<Bath className="w-4 h-4 text-muted-foreground" />}
          label="Baths"
          value={property.bathrooms?.toString() ?? "-"}
        />
        <AttributeTile
          icon={<Car className="w-4 h-4 text-muted-foreground" />}
          label="Parking"
          value={property.parking?.toString() ?? "-"}
        />
        <AttributeTile
          icon={<Ruler className="w-4 h-4 text-muted-foreground" />}
          label="Size"
          value={
            property.property_size
              ? `${property.property_size}${property.property_size_unit ? ` ${property.property_size_unit}` : ""}`
              : "-"
          }
        />
        <AttributeTile
          icon={<Ruler className="w-4 h-4 text-muted-foreground" />}
          label="Land"
          value={latestLand ? `${latestLand}${latestLandUnit ? ` ${latestLandUnit}` : ""}` : "-"}
        />
      </div>

      {/* Price summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <DollarSign className="w-4 h-4 text-green-500" />
              <span>Last sale price</span>
            </div>
            <p className="text-2xl font-bold mt-1">{formatPrice(property.last_sale_price)}</p>
            {property.last_sale_date && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {formatDate(property.last_sale_date)}
              </p>
            )}
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <History className="w-4 h-4" />
              <span>Snapshots</span>
            </div>
            <p className="text-2xl font-bold mt-1">{property.snapshot_count}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {property.scrape_count} scrapes total
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <span>Last scraped</span>
            </div>
            <p className="text-base font-medium mt-1">
              {formatDate(property.last_scraped_at, { includeTime: true })}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              first seen {formatDate(property.first_seen_at)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Price history chart */}
      {chartData.length >= 2 && (
        <Card>
          <CardContent className="p-4">
            <p className="text-sm font-semibold mb-3 flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-500" />
              Price history
            </p>
            <ChartContainer
              config={{ price: { label: "Price", color: "hsl(var(--primary))" } }}
              className="h-[240px] w-full"
            >
              <LineChart data={chartData} accessibilityLayer>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  tickFormatter={(value) => formatDate(value)}
                  tick={{ fontSize: 11 }}
                />
                <YAxis
                  tickFormatter={(value: number) => formatPrice(value)}
                  tick={{ fontSize: 11 }}
                  width={70}
                />
                <ChartTooltip content={<ChartTooltipContent />} />
                <Line
                  type="monotone"
                  dataKey="price"
                  stroke="var(--color-price)"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ChartContainer>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold">Data completeness</h2>
            <Badge variant={completeness.missing.length ? "outline" : "default"}>
              {completeness.present}/{completeness.total} core fields
            </Badge>
          </div>
          {completeness.missing.length > 0 && (
            <p className="text-sm text-muted-foreground">
              Missing: {completeness.missing.join(", ")}
            </p>
          )}
          {completeness.flags.length > 0 && (
            <p className="text-sm text-destructive">
              Possible data issues: {completeness.flags.join("; ")}
            </p>
          )}
          <p className="text-xs text-muted-foreground">
            Simple field checks only; see Poisoned Data for full detection.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-3">
          <h2 className="font-semibold">Sale history ({sales.length})</h2>
          {sales.length === 0 ? (
            <p className="text-sm text-muted-foreground">No dated sold snapshots.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2">Sale date</th>
                    <th>Price</th>
                    <th>Display</th>
                    <th>Adapter</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.map((sale) => (
                    <tr key={sale.id} className="border-b">
                      <td className="py-2">{formatDate(sale.sale_date)}</td>
                      <td>{formatPrice(sale.sale_price)}</td>
                      <td>{sale.price_display ?? "—"}</td>
                      <td>{sale.adapter_slug ?? "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <PropertyPvReport property={property} />
      <PropertyAssets snapshotIds={snapshots.map((snapshot) => snapshot.id)} />
      <PropertyEvidence snapshots={snapshots} />

      {/* Fetch info preview — last run of the "Fetch info" button. */}
      {fetchInfoResult && fetchInfoResult.length > 0 ? (
        <Card>
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium">
              <Sparkles className="w-4 h-4 text-primary" />
              Live fetch preview
              <span className="text-xs text-muted-foreground font-normal">
                — not persisted as snapshots. Canonical ingestion runs via the queue worker.
              </span>
            </div>
            <div className="grid gap-2">
              {fetchInfoResult.map((a, i) => (
                <div key={`${a.url}-${i}`} className="rounded-md border p-3 text-xs space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge
                      className={cn(
                        "text-[10px]",
                        a.status === "success"
                          ? "bg-green-100 text-green-800"
                          : a.status === "failed"
                            ? "bg-red-100 text-red-800"
                            : "bg-gray-100 text-gray-800",
                      )}
                    >
                      {a.status}
                    </Badge>
                    {a.adapterSlug && (
                      <Badge variant="outline" className="text-[10px] font-mono">
                        {a.adapterSlug}
                      </Badge>
                    )}
                    {a.provider && <span className="text-muted-foreground">via {a.provider}</span>}
                    <a
                      href={a.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-auto text-primary hover:underline inline-flex items-center gap-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      source
                    </a>
                  </div>
                  {a.error && <p className="text-destructive">{a.error}</p>}
                  {a.extracted && (
                    <pre className="bg-muted p-2 rounded overflow-x-auto max-h-48">
                      {JSON.stringify(a.extracted, null, 2)}
                    </pre>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
};

interface AttributeTileProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function AttributeTile({ icon, label, value }: AttributeTileProps) {
  return (
    <Card>
      <CardContent className="p-3 flex items-center gap-2">
        {icon}
        <div>
          <p className="text-[10px] text-muted-foreground uppercase">{label}</p>
          <p className="text-sm font-medium">{value}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export default PropertyDetail;
